/* eslint-disable max-lines-per-function */
import "./insTimeSeriesChart.scss";
import Highcharts from "highcharts";
import hcMore from "highcharts/highcharts-more";
import { merge } from "lodash";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { formatPointLabel, getChartSelectValueProps, useGetBenchmarkFooter } from "../base/utils";
import { formatToQuarterForXAxis, getLabelValuePairs, getTicksPositions, yAxisFormatter } from "../insDualAxisChart/utils";
import { InsHighcharts } from "components";
import { InsLabelValueTooltip } from "components/widgets/base/common/insLabelValueTooltip";
import { InsQueryStatedElement } from "components/widgets/base/insQueryStatedElement";
import { useSelectedValues } from "components/widgets/hooks/use-current-widget";
import { ChartSelectValueFormatKind } from "store/current-dashboard/types";
hcMore(Highcharts);
const getClassName = (s) => {
    const classes = [];
    if (s?.custom?.monochrome) {
        classes.push("highcharts-monochrome-series");
    }
    if (s?.custom?.striped) {
        if (s.type === "line") {
            classes.push("highcharts-striped-lines-series");
        }
        else if (s.type === "column") {
            classes.push("highcharts-striped-columns-series");
        }
    }
    if (s?.className) {
        classes.push(s.className);
    }
    return classes.join(" ");
};
const getLabelForCategoryType = (point, selectedCurrency, format) => [
    [
        "Period",
        point.name
    ],
    [
        point.series.name,
        formatPointLabel(point.y, format, undefined, { currency: selectedCurrency })
    ]
];
export const InsTimeSeriesChartBase = (props) => {
    const selectedValues = useSelectedValues(props.widgetFactoryProps.sectionId, props.widgetFactoryProps.widgetId);
    const chartSelectValues = getChartSelectValueProps(selectedValues, props.widgetProps)
        .map((value, index) => ({ ...value, order: (props.widgetProps?.dataSelectProps?.[index] && props.widgetProps.dataSelectProps[index].order) || 0 })), plotOptions = props.dataPointData?.additionalData?.plotOptions || {}, series = props.dataPointData?.data, xaxis = props.dataPointData?.additionalData?.xaxis || { tickPositions: undefined }, yaxis = (props.dataPointData?.additionalData?.yaxis || []);
    const hasData = Boolean(series?.some((s) => s?.data?.length));
    const stripedSeries = series?.filter((s) => s.custom?.striped && s.type === "column").map((i) => i.colorIndex);
    const options = {
        chart: {
            // Note: in Highchart documentation and description of this property value is boolean but it asks for object
            alignThresholds: true,
            animation: false,
            className: `ins-time-series-chart ${[...new Set(series?.map((s) => s.type))].length > 1 ? "highcharts-mixed-series" : ""}`,
            styledMode: true
        },
        credits: { enabled: false },
        legend: { align: "left", enabled: props.widgetProps.showLegend !== false, maxHeight: 85, x: 28 },
        plotOptions: merge({
            area: { stacking: "normal" },
            arearange: { animation: false, marker: { enabled: series?.every((s) => s?.data?.length === 1) || false }, stickyTracking: false },
            column: { animation: false, stacking: props?.widgetProps?.unstack ? undefined : "normal", stickyTracking: false, zIndex: 1 },
            line: { animation: false, marker: { enabled: !series?.some((s) => s.markers) }, stickyTracking: false, zIndex: 1 }
        }, plotOptions),
        series: series?.map((s, i) => ({ ...s, className: getClassName(s), name: s.name || chartSelectValues[i]?.displayName, threshold: 0 })),
        subtitle: {
            align: "left",
            text: useGetBenchmarkFooter(props.widgetProps.subtitle, props.dataPointData?.additionalData),
            verticalAlign: "bottom",
            x: -8
        },
        title: { text: "" },
        tooltip: {
            formatter() {
                let valueFormatFromAxis = props.widgetProps.valueFormat;
                if (!valueFormatFromAxis && series && this.series?.name) {
                    const serieYAxis = series.find((s) => s.name === this.series.name)?.yAxis;
                    valueFormatFromAxis = Number.isInteger(serieYAxis) ? yaxis[Number(serieYAxis)].valueFormat : valueFormatFromAxis;
                }
                const tooltip = props.isCategory ? getLabelForCategoryType(this.point, props.selectedCurrency, valueFormatFromAxis) : this?.point?.options?.custom?.tooltip;
                return renderToStaticMarkup(React.createElement(InsLabelValueTooltip, { labelValuePairs: tooltip?.length
                        ? tooltip
                        : getLabelValuePairs(this, chartSelectValues.length === 0 ? series : chartSelectValues, props.selectedCurrency, props.widgetProps.customMapping, valueFormatFromAxis) }));
            },
            useHTML: true
        },
        xAxis: props.isCategory
            ? props.dataPointData?.additionalData?.xaxis ?? { type: "category" }
            : {
                labels: {
                    formatter() {
                        return formatToQuarterForXAxis(this, props.currentGranularity, true);
                    }
                },
                min: xaxis.tickPositions ? Math.min(...xaxis.tickPositions ?? []) : undefined,
                tickPositioner() {
                    return getTicksPositions(this, props.currentGranularity, xaxis.tickPositions, props.dataPointData);
                },
                type: "datetime",
                units: [["month", [3, 6]], ["year", [1]]]
            },
        yAxis: yaxis.length
            ? yaxis.map((axis, index) => ({
                ...axis,
                className: axis.opposite ? "secondSerie" : "",
                labels: {
                    formatter() {
                        return yAxisFormatter(this, props.selectedCurrency, chartSelectValues[index]?.valueFormat
                            ? chartSelectValues[index]?.valueFormat
                            : axis.valueFormat, axis.valueUnit, axis.limits);
                    }
                },
                softMin: axis.valueFormat === ChartSelectValueFormatKind.esg ? 1 : 0,
                title: { text: axis.name || chartSelectValues[index]?.displayName || props.widgetProps.yDisplayName }
            }))
            : {
                labels: {
                    formatter() {
                        return yAxisFormatter(this, props.selectedCurrency, chartSelectValues[0]?.valueFormat ?? props.widgetProps.valueFormat, chartSelectValues[0]?.valueUnit, chartSelectValues[0]?.axisLimits);
                    }
                },
                softMin: 0,
                title: { text: props.widgetProps.yDisplayName }
            }
    };
    return React.createElement(InsQueryStatedElement, { widgetFactoryProps: props.widgetFactoryProps, queryState: props.queryState, hasData: hasData },
        React.createElement(InsHighcharts, { colorSet: props.widgetProps.colorSet, options: options, stripedColors: stripedSeries, ...props.widgetFactoryProps }));
};
